import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import AcoesFatura from './AcoesFatura';
import FaturaDetalhes from './FaturaDetalhes';
import {
  STATUS_ABERTOS, STATUS_PAGOS, classeStatusFatura, competenciaAnterior, erroDaFatura,
  formatarCompetencia, formatarTelefone, linkWhatsapp, moedaBRL, textoStatusFatura
} from './faturas-utils';
import './master-faturamento.css';

const POR_PAGINA = 50;

// Agrupamento usado nos cartões e filtros da lista de faturas.
const grupoFatura = f => {
  if (f.cancelado || f.status === 'CANCELLED') return 'canceladas';
  if (f.status === 'ERROR' || f.status === 'PROCESSING') return 'erro';
  if (STATUS_PAGOS.includes(f.status)) return 'pagas';
  if (f.status === 'OVERDUE' || (STATUS_ABERTOS.includes(f.status) && Number(f.dias_atraso) > 0)) return 'vencidas';
  if (STATUS_ABERTOS.includes(f.status)) return 'abertas';
  return 'outras';
};

const CARTOES_FATURAS = [
  { grupo: 'todas', rotulo: 'Todas', classe: '' },
  { grupo: 'abertas', rotulo: 'Em aberto (a vencer)', classe: 'warn' },
  { grupo: 'vencidas', rotulo: 'Vencidas', classe: 'erro' },
  { grupo: 'pagas', rotulo: 'Pagas', classe: 'ok' },
  { grupo: 'erro', rotulo: 'Erro na geração', classe: 'erro' },
  { grupo: 'canceladas', rotulo: 'Canceladas', classe: '' }
];

const dataHora = valor => (valor ? new Date(String(valor).replace(' ', 'T')).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const Contato = ({ email, whatsapp }) => {
  const wa = linkWhatsapp(whatsapp);
  return (
    <div className="mf-contato">
      {email ? <a href={`mailto:${email}`}>{email}</a> : <span className="mf-muted">sem e-mail</span>}
      {whatsapp
        ? (wa ? <a href={wa} target="_blank" rel="noopener noreferrer">{formatarTelefone(whatsapp)}</a> : <span>{whatsapp}</span>)
        : <span className="mf-muted">sem telefone</span>}
    </div>
  );
};

const exportarCsv = (nomeArquivo, cabecalho, linhas) => {
  const celula = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const conteudo = [cabecalho, ...linhas].map(l => l.map(celula).join(';')).join('\r\n');
  const blob = new Blob(['﻿' + conteudo], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nomeArquivo;
  a.click();
  URL.revokeObjectURL(url);
};

export default function MasterFaturamento({ api, onAtualizar, onAbrirAba }) {
  const [secao, setSecao] = useState('fechamento');
  const [faturas, setFaturas] = useState([]);
  const [carregandoFaturas, setCarregandoFaturas] = useState(true);
  const [erroFaturas, setErroFaturas] = useState('');

  // Fechamento mensal
  const [competencia, setCompetencia] = useState(competenciaAnterior());
  const [apuracao, setApuracao] = useState(null);
  const [apurando, setApurando] = useState(false);
  const [erroApuracao, setErroApuracao] = useState('');
  const [buscaFechamento, setBuscaFechamento] = useState('');
  const [ocultarSemUso, setOcultarSemUso] = useState(false);
  const [shExpandida, setShExpandida] = useState(null);
  const [processamento, setProcessamento] = useState(null);

  // Lista de faturas
  const [busca, setBusca] = useState('');
  const [filtroCompetencia, setFiltroCompetencia] = useState('');
  const [grupo, setGrupo] = useState('todas');
  const [pagina, setPagina] = useState(1);
  const [faturaExpandida, setFaturaExpandida] = useState(null);
  const [ocupado, setOcupado] = useState(false);

  const carregarFaturas = useCallback(async () => {
    setCarregandoFaturas(true);
    try {
      const resposta = await api.get('/admin/faturas');
      if (!Array.isArray(resposta)) throw new Error(resposta?.error || 'Falha ao carregar faturas');
      setFaturas(resposta);
      setErroFaturas('');
    } catch (e) {
      setErroFaturas(e.message);
    } finally {
      setCarregandoFaturas(false);
    }
  }, [api]);

  const apurar = useCallback(async (mesReferencia) => {
    const [ano, mes] = mesReferencia.split('-');
    setApurando(true);
    setErroApuracao('');
    try {
      const resposta = await api.post('/faturas/gerar-mensais', { mes, ano, simular: true });
      if (!resposta?.success) throw new Error(resposta?.error || 'Falha ao apurar a competência');
      setApuracao(resposta);
    } catch (e) {
      setApuracao(null);
      setErroApuracao(e.message);
    } finally {
      setApurando(false);
    }
  }, [api]);

  useEffect(() => { carregarFaturas(); }, [carregarFaturas]);
  useEffect(() => { if (/^\d{4}-\d{2}$/.test(competencia)) apurar(competencia); }, [competencia, apurar]);

  const recarregarTudo = async () => {
    await Promise.all([carregarFaturas(), apurar(competencia)]);
    onAtualizar?.();
  };

  // ---------- Fechamento: junta apuração com as faturas já existentes ----------
  const linhasFechamento = useMemo(() => {
    const resultados = apuracao?.resultados || [];
    return resultados.map(r => {
      const fatura = faturas
        .filter(f => f.software_house_id === r.software_house_id && f.mes_referencia === competencia && f.tipo_cobranca === 'MENSAL')
        .sort((a, b) => b.id - a.id)[0] || null;
      let situacao;
      if (fatura && fatura.status === 'ERROR') situacao = 'erro';
      else if (fatura && (fatura.cancelado || fatura.status === 'CANCELLED')) situacao = 'cancelada';
      else if (fatura) situacao = 'faturada';
      else if (!r.quantidade_aplicativos || r.valor_total <= 0) situacao = 'sem-uso';
      else situacao = 'a-gerar';
      return { ...r, fatura, situacao };
    });
  }, [apuracao, faturas, competencia]);

  const termoFechamento = buscaFechamento.trim().toLowerCase();
  const fechamentoVisivel = linhasFechamento.filter(l =>
    (!ocultarSemUso || l.situacao !== 'sem-uso') &&
    (!termoFechamento || `${l.software_house} ${l.software_house_id} ${l.email || ''} ${l.whatsapp || ''}`.toLowerCase().includes(termoFechamento)));
  const aGerar = linhasFechamento.filter(l => l.situacao === 'a-gerar' || l.situacao === 'erro');
  const somar = (lista, campo = 'valor_total') => lista.reduce((s, l) => s + Number(l[campo] || 0), 0);
  const faturadas = linhasFechamento.filter(l => l.situacao === 'faturada');
  const recebidoCompetencia = faturadas.filter(l => STATUS_PAGOS.includes(l.fatura.status)).reduce((s, l) => s + Number(l.fatura.valor_pago || l.fatura.valor || 0), 0);

  const gerarFaturas = async (lista) => {
    if (!lista.length) return Swal.fire('Nada a gerar', 'Não há Software House com cobrança pendente de geração nesta competência.', 'info');
    const confirmacao = await Swal.fire({
      title: `Gerar ${lista.length} fatura(s)?`,
      html: `Competência <strong>${formatarCompetencia(competencia)}</strong> · vencimento <strong>${apuracao?.vencimento?.split('-').reverse().join('/')}</strong><br/>
        Valor total: <strong>${moedaBRL(somar(lista))}</strong><br/><br/>
        Os boletos serão emitidos de verdade no Asaas.`,
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Sim, gerar', cancelButtonText: 'Cancelar'
    });
    if (!confirmacao.isConfirmed) return;

    const [ano, mes] = competencia.split('-');
    const log = [];
    setProcessamento({ ativo: true, total: lista.length, feitos: 0, log: [] });
    // Uma SH por vez: o progresso fica visível e uma falha não interrompe as demais.
    for (const linha of lista) {
      let item;
      try {
        const resposta = await api.post('/faturas/gerar-mensais', { mes, ano, software_house_id: linha.software_house_id, simular: false });
        const r = resposta?.resultados?.[0];
        if (!resposta?.success || !r) item = { tipo: 'erro', mensagem: resposta?.error || 'Sem resposta do servidor' };
        else if (r.gerada) item = { tipo: 'ok', mensagem: `Fatura #${r.fatura_id} gerada (${moedaBRL(r.valor_total)})` };
        else if (r.sucesso) item = { tipo: 'info', mensagem: r.motivo || 'Nada a gerar' };
        else item = { tipo: 'erro', mensagem: r.erro || 'Falha ao gerar' };
      } catch (e) {
        item = { tipo: 'erro', mensagem: e.message };
      }
      log.push({ id: linha.software_house_id, nome: linha.software_house, ...item });
      setProcessamento({ ativo: true, total: lista.length, feitos: log.length, log: [...log] });
    }
    setProcessamento({ ativo: false, total: lista.length, feitos: log.length, log: [...log], concluidoEm: new Date() });
    await recarregarTudo();
    const erros = log.filter(l => l.tipo === 'erro').length;
    Swal.fire(erros ? 'Concluído com erros' : 'Faturas geradas',
      `${log.filter(l => l.tipo === 'ok').length} gerada(s), ${erros} erro(s). Veja o log do processamento.`, erros ? 'warning' : 'success');
  };

  // ---------- Ações administrativas ----------
  const alterarBloqueio = async (id, nome, bloquear) => {
    const confirmacao = await Swal.fire({
      title: bloquear ? `Bloquear ${nome}?` : `Liberar ${nome}?`,
      text: bloquear
        ? 'Os aplicativos e integrações desta Software House serão suspensos. O portal continua acessível para pagar.'
        : 'Os aplicativos e integrações serão liberados. Isto não quita as faturas em aberto.',
      icon: bloquear ? 'warning' : 'question', showCancelButton: true,
      confirmButtonText: bloquear ? 'Sim, bloquear' : 'Sim, liberar', cancelButtonText: 'Cancelar'
    });
    if (!confirmacao.isConfirmed) return;
    setOcupado(true);
    try {
      const r = await api.post('/admin/software-houses/bloqueio-financeiro', { ids: [id], bloquear });
      if (!r?.success) throw new Error(r?.error || 'Falha ao alterar acesso');
      Swal.fire('Concluído', r.message, 'success');
      await recarregarTudo();
    } catch (e) {
      Swal.fire('Erro', e.message, 'error');
    } finally {
      setOcupado(false);
    }
  };

  const cancelarFatura = async (fatura) => {
    const { value: motivo, isConfirmed } = await Swal.fire({
      title: `Cancelar fatura #${fatura.id}?`,
      html: `${fatura.titular_nome} · ${formatarCompetencia(fatura.mes_referencia)} · ${moedaBRL(fatura.valor)}<br/>A cobrança será removida no Asaas.`,
      input: 'text', inputLabel: 'Motivo do cancelamento', inputPlaceholder: 'Ex.: cobrança indevida, acordo comercial…',
      inputValidator: v => (!v || !v.trim() ? 'Informe o motivo' : undefined),
      icon: 'warning', showCancelButton: true, confirmButtonText: 'Cancelar fatura', cancelButtonText: 'Voltar'
    });
    if (!isConfirmed) return;
    setOcupado(true);
    try {
      const r = await api.post(`/admin/faturas/${fatura.id}/cancelar`, { motivo });
      if (!r?.success) throw new Error(r?.error || 'Falha ao cancelar');
      Swal.fire('Cancelada', r.message, 'success');
      await recarregarTudo();
    } catch (e) {
      Swal.fire('Erro', e.message, 'error');
    } finally {
      setOcupado(false);
    }
  };

  const sincronizarAsaas = async () => {
    setOcupado(true);
    try {
      const r = await api.post('/faturas/sincronizar', { todas: true });
      if (r?.error) throw new Error(r.error);
      Swal.fire('Sincronizado', r?.message || 'Status atualizados', 'success');
      await recarregarTudo();
    } catch (e) {
      Swal.fire('Erro', e.message || 'Falha ao sincronizar com o Asaas', 'error');
    } finally {
      setOcupado(false);
    }
  };

  const copiarLink = async (fatura) => {
    const url = fatura.invoice_url || fatura.bank_slip_url || fatura.boleto_url;
    if (!url) return Swal.fire('Aviso', 'Fatura sem link no Asaas', 'warning');
    await navigator.clipboard.writeText(url);
    Swal.fire({ toast: true, position: 'top-end', timer: 2000, showConfirmButton: false, icon: 'success', title: 'Link da fatura copiado' });
  };

  // ---------- Lista de faturas ----------
  const competencias = useMemo(() => [...new Set(faturas.map(f => f.mes_referencia).filter(Boolean))].sort().reverse(), [faturas]);
  const termo = busca.trim().toLowerCase();
  const faturasFiltroBase = faturas.filter(f =>
    (!filtroCompetencia || f.mes_referencia === filtroCompetencia) &&
    (!termo || `${f.titular_nome || ''} ${f.titular_fantasia || ''} ${f.titular_email || ''} ${f.software_house_id || ''} #${f.id} ${f.asaas_payment_id || ''}`.toLowerCase().includes(termo)));
  const faturasFiltradas = grupo === 'todas' ? faturasFiltroBase : faturasFiltroBase.filter(f => grupoFatura(f) === grupo);
  const totalPaginas = Math.max(1, Math.ceil(faturasFiltradas.length / POR_PAGINA));
  const paginaAtual = Math.min(pagina, totalPaginas);
  const faturasPagina = faturasFiltradas.slice((paginaAtual - 1) * POR_PAGINA, paginaAtual * POR_PAGINA);
  const resumoGrupo = g => {
    const lista = g === 'todas' ? faturasFiltroBase : faturasFiltroBase.filter(f => grupoFatura(f) === g);
    return { quantidade: lista.length, valor: lista.reduce((s, f) => s + Number(f.valor || 0), 0) };
  };

  const exportarFaturas = () => exportarCsv(`faturas-${filtroCompetencia || 'todas'}.csv`,
    ['Fatura', 'SH ID', 'Software House', 'E-mail', 'Telefone', 'Competência', 'Apps', 'Valor', 'Vencimento', 'Status', 'Dias atraso', 'Pago em', 'Valor pago', 'Acesso', 'ID Asaas', 'Link'],
    faturasFiltradas.map(f => [f.id, f.software_house_id, f.titular_nome, f.titular_email, f.titular_whatsapp, formatarCompetencia(f.mes_referencia),
      f.quantidade_aplicativos, Number(f.valor || 0).toFixed(2).replace('.', ','), f.vencimento_formatado, textoStatusFatura(f.status), f.dias_atraso || 0,
      f.data_pagamento_formatada || '', f.valor_pago != null ? Number(f.valor_pago).toFixed(2).replace('.', ',') : '',
      f.bloqueio_financeiro ? 'Bloqueado' : 'Liberado', f.asaas_payment_id || '', f.invoice_url || '']));

  const exportarFechamento = () => exportarCsv(`fechamento-${competencia}.csv`,
    ['SH ID', 'Software House', 'E-mail', 'Telefone', 'Apps utilizados', 'Valor por app', 'Valor', 'Vencimento', 'Situação', 'Fatura', 'Status fatura'],
    linhasFechamento.map(l => [l.software_house_id, l.software_house, l.email, l.whatsapp, l.quantidade_aplicativos,
      Number(l.valor_unitario || 0).toFixed(2).replace('.', ','), Number(l.valor_total || 0).toFixed(2).replace('.', ','),
      l.vencimento?.split('-').reverse().join('/'), ROTULO_SITUACAO[l.situacao], l.fatura?.id || '', l.fatura ? textoStatusFatura(l.fatura.status) : '']));

  const BotaoBloqueio = ({ id, nome, bloqueado }) => (
    <button type="button" className={`mf-btn-mini ${bloqueado ? 'is-liberar' : 'is-bloquear'}`} disabled={ocupado}
      title={bloqueado ? 'Liberar aplicativos e integrações' : 'Bloquear aplicativos e integrações'}
      onClick={() => alterarBloqueio(id, nome, !bloqueado)}>
      {bloqueado ? 'Liberar' : 'Bloquear'}
    </button>
  );

  const percentual = processamento ? Math.round((processamento.feitos / Math.max(processamento.total, 1)) * 100) : 0;

  return (
    <section className="mf">
      <div className="mf-head">
        <div>
          <span className="eyebrow">FATURAMENTO</span>
          <h3>Fechamento mensal e faturas das Software Houses</h3>
          <p>Apure o uso, gere as cobranças no Asaas e acompanhe pagamentos, atrasos e bloqueios.</p>
        </div>
        <div className="mf-head-actions">
          <button type="button" className="btn-secondary" onClick={sincronizarAsaas} disabled={ocupado}>Sincronizar status com Asaas</button>
          {onAbrirAba && <button type="button" className="btn-secondary" onClick={() => onAbrirAba('inadimplentes')}>Inadimplentes / bloqueios</button>}
        </div>
      </div>

      <div className="mf-segment" role="tablist" aria-label="Seções do faturamento">
        <button type="button" role="tab" aria-selected={secao === 'fechamento'} className={secao === 'fechamento' ? 'is-active' : ''} onClick={() => setSecao('fechamento')}>
          Fechamento por Software House
        </button>
        <button type="button" role="tab" aria-selected={secao === 'faturas'} className={secao === 'faturas' ? 'is-active' : ''} onClick={() => setSecao('faturas')}>
          Todas as faturas <small>{faturas.length}</small>
        </button>
      </div>

      {secao === 'fechamento' && <>
        <div className="mf-toolbar">
          <label className="mf-field">
            <span>Competência (mês de uso)</span>
            <input type="month" value={competencia} onChange={e => setCompetencia(e.target.value)} />
          </label>
          <div className="mf-field mf-info">
            <span>Vencimento</span>
            <strong>{apuracao?.vencimento ? apuracao.vencimento.split('-').reverse().join('/') : '—'}</strong>
          </div>
          <div className="mf-toolbar-actions">
            <button type="button" className="btn-secondary" onClick={() => apurar(competencia)} disabled={apurando || processamento?.ativo}>
              {apurando ? 'Apurando…' : 'Reapurar'}
            </button>
            <button type="button" className="btn-secondary" onClick={exportarFechamento} disabled={!linhasFechamento.length}>Exportar CSV</button>
            <button type="button" className="btn-primary" onClick={() => gerarFaturas(aGerar)} disabled={apurando || processamento?.ativo || !aGerar.length}>
              {processamento?.ativo ? 'Gerando…' : `Gerar faturas (${aGerar.length})`}
            </button>
          </div>
        </div>

        {erroApuracao && <div className="master-error" role="alert">{erroApuracao}</div>}

        <section className="pg-kpis mf-kpis" aria-label="Resumo do fechamento">
          <div className="pg-kpi"><span>SHs avaliadas</span><strong>{linhasFechamento.length}</strong><small>{linhasFechamento.filter(l => l.quantidade_aplicativos > 0).length} com uso no mês</small></div>
          <div className="pg-kpi"><span>Apps utilizados</span><strong>{somar(linhasFechamento, 'quantidade_aplicativos')}</strong><small>na competência {formatarCompetencia(competencia)}</small></div>
          <div className="pg-kpi pg-kpi-pago"><span>Valor apurado</span><strong>{moedaBRL(somar(linhasFechamento))}</strong><small>soma de todas as SHs</small></div>
          <div className="pg-kpi pg-kpi-ok"><span>Já faturado</span><strong>{moedaBRL(faturadas.reduce((s, l) => s + Number(l.fatura.valor || 0), 0))}</strong><small>{faturadas.length} fatura(s) · recebido {moedaBRL(recebidoCompetencia)}</small></div>
          <div className="pg-kpi pg-kpi-warn"><span>A gerar</span><strong>{aGerar.length}</strong><small>{moedaBRL(somar(aGerar))}{linhasFechamento.some(l => l.situacao === 'erro') ? ` · ${linhasFechamento.filter(l => l.situacao === 'erro').length} com erro` : ''}</small></div>
        </section>

        {processamento && (
          <section className={`mf-processo ${processamento.ativo ? 'is-ativo' : ''}`} aria-live="polite">
            <header>
              <strong>{processamento.ativo ? 'Processando faturas…' : `Processamento concluído${processamento.concluidoEm ? ` às ${processamento.concluidoEm.toLocaleTimeString('pt-BR')}` : ''}`}</strong>
              <span>{processamento.feitos} de {processamento.total} · {percentual}%</span>
              {!processamento.ativo && <button type="button" className="mf-btn-mini" onClick={() => setProcessamento(null)}>Fechar log</button>}
            </header>
            <div className="mf-progress" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={percentual}><div style={{ width: `${percentual}%` }} /></div>
            <ul>
              {processamento.log.map(item => (
                <li key={item.id} className={`is-${item.tipo}`}>
                  <span aria-hidden="true">{item.tipo === 'ok' ? '✔' : item.tipo === 'erro' ? '✖' : '•'}</span>
                  <strong>{item.nome}</strong> <small>SH #{item.id}</small> — {item.mensagem}
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="master-filters">
          <input type="search" aria-label="Buscar Software House" placeholder="Buscar por Software House, ID, e-mail ou telefone"
            value={buscaFechamento} onChange={e => setBuscaFechamento(e.target.value)} />
          <label className="mf-check"><input type="checkbox" checked={ocultarSemUso} onChange={e => setOcultarSemUso(e.target.checked)} /> Ocultar SHs sem uso</label>
          <span>{fechamentoVisivel.length} Software House(s)</span>
        </div>

        <div className="dashboard-master-table-container">
          <table className="dashboard-master-table mf-table">
            <thead>
              <tr>
                <th aria-label="Detalhes" />
                <th>Software House</th>
                <th>Contato</th>
                <th>Apps utilizados</th>
                <th>Valor cobrado</th>
                <th>Vencimento</th>
                <th>Situação</th>
                <th>Acesso</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {apurando && !linhasFechamento.length && <tr><td colSpan="9">Apurando a competência…</td></tr>}
              {!apurando && !fechamentoVisivel.length && <tr><td colSpan="9">Nenhuma Software House neste filtro.</td></tr>}
              {fechamentoVisivel.map(l => (
                <React.Fragment key={l.software_house_id}>
                  <tr className={l.bloqueio_financeiro ? 'row-blocked' : ''}>
                    <td>
                      <button type="button" className="sh-expand-button" aria-expanded={shExpandida === l.software_house_id}
                        aria-label={`${shExpandida === l.software_house_id ? 'Recolher' : 'Ver'} apps de ${l.software_house}`}
                        onClick={() => setShExpandida(a => (a === l.software_house_id ? null : l.software_house_id))}>
                        {shExpandida === l.software_house_id ? '−' : '+'}
                      </button>
                    </td>
                    <td><strong>{l.software_house}</strong><small className="table-secondary">SH #{l.software_house_id} · {l.plano}</small></td>
                    <td><Contato email={l.email} whatsapp={l.whatsapp} /></td>
                    <td><strong>{l.quantidade_aplicativos}</strong><small className="table-secondary">× {moedaBRL(l.valor_unitario)}</small></td>
                    <td><strong>{moedaBRL(l.fatura ? l.fatura.valor : l.valor_total)}</strong>
                      {l.fatura && Number(l.fatura.valor) !== Number(l.valor_total) && <small className="table-secondary" title="O uso apurado hoje difere do valor faturado">apurado hoje: {moedaBRL(l.valor_total)}</small>}
                    </td>
                    <td>{l.fatura?.vencimento_formatado || (l.situacao === 'sem-uso' ? '—' : l.vencimento?.split('-').reverse().join('/'))}</td>
                    <td><SituacaoFechamento linha={l} /></td>
                    <td><span className={`status-badge ${l.bloqueio_financeiro ? 'bloqueado' : 'ativo'}`}>{l.bloqueio_financeiro ? 'Bloqueado' : 'Liberado'}</span></td>
                    <td>
                      <div className="mf-acoes">
                        {l.fatura && <AcoesFatura fatura={l.fatura} api={api} />}
                        {(l.situacao === 'a-gerar' || l.situacao === 'erro') && (
                          <button type="button" className="mf-btn-mini is-primario" disabled={processamento?.ativo} onClick={() => gerarFaturas([l])}>
                            {l.situacao === 'erro' ? 'Reprocessar' : 'Gerar esta'}
                          </button>
                        )}
                        <BotaoBloqueio id={l.software_house_id} nome={l.software_house} bloqueado={l.bloqueio_financeiro} />
                      </div>
                    </td>
                  </tr>
                  {shExpandida === l.software_house_id && (
                    <tr className="fatura-detalhe-row">
                      <td colSpan="9">
                        {l.fatura
                          ? <FaturaDetalhes fatura={l.fatura} api={api} />
                          : <AppsApurados aplicativos={l.aplicativos} competencia={competencia} valorUnitario={l.valor_unitario} />}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mf-nota">
          A apuração considera Software Houses <strong>ativas</strong> com plano pago e conta os aplicativos de produção que enviaram carga,
          solicitaram carga ou enviaram pedidos no mês, inclusive os inativados após o uso. Apps de homologação não entram.
        </p>
      </>}

      {secao === 'faturas' && <>
        {erroFaturas && <div className="master-error" role="alert">{erroFaturas}</div>}
        <section className="pg-kpis mf-kpis mf-kpis-6" aria-label="Resumo das faturas">
          {CARTOES_FATURAS.map(c => {
            const r = resumoGrupo(c.grupo);
            return (
              <button type="button" key={c.grupo} aria-pressed={grupo === c.grupo}
                className={`pg-kpi ${c.classe ? `pg-kpi-${c.classe}` : ''} ${grupo === c.grupo ? 'is-active' : ''}`}
                onClick={() => { setGrupo(c.grupo); setPagina(1); }}>
                <span>{c.rotulo}</span><strong>{r.quantidade}</strong><small>{moedaBRL(r.valor)}</small>
              </button>
            );
          })}
        </section>

        <div className="master-filters">
          <input type="search" aria-label="Buscar fatura" placeholder="Buscar por Software House, e-mail, ID da fatura ou ID Asaas"
            value={busca} onChange={e => { setBusca(e.target.value); setPagina(1); }} />
          <select aria-label="Competência" value={filtroCompetencia} onChange={e => { setFiltroCompetencia(e.target.value); setPagina(1); }}>
            <option value="">Todas as competências</option>
            {competencias.map(m => <option key={m} value={m}>{formatarCompetencia(m)}</option>)}
          </select>
          <button type="button" className="btn-secondary" onClick={exportarFaturas} disabled={!faturasFiltradas.length}>Exportar CSV</button>
          <span>{faturasFiltradas.length} fatura(s)</span>
        </div>

        <div className="dashboard-master-table-container">
          <table className="dashboard-master-table mf-table">
            <thead>
              <tr>
                <th aria-label="Detalhes" />
                <th>Software House</th>
                <th>Contato</th>
                <th>Competência</th>
                <th>Apps</th>
                <th>Valor</th>
                <th>Vencimento</th>
                <th>Status</th>
                <th>Acesso</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {carregandoFaturas && !faturas.length && <tr><td colSpan="10">Carregando faturas…</td></tr>}
              {!carregandoFaturas && !faturasFiltradas.length && <tr><td colSpan="10">Nenhuma fatura neste filtro.</td></tr>}
              {faturasPagina.map(f => {
                const erro = erroDaFatura(f);
                const podeCancelar = !f.cancelado && !STATUS_PAGOS.includes(f.status) && f.status !== 'CANCELLED';
                return (
                  <React.Fragment key={f.id}>
                    <tr className={f.bloqueio_financeiro ? 'row-blocked' : ''}>
                      <td>
                        <button type="button" className="sh-expand-button" aria-expanded={faturaExpandida === f.id}
                          aria-label={`${faturaExpandida === f.id ? 'Recolher' : 'Ver'} detalhes da fatura ${f.id}`}
                          onClick={() => setFaturaExpandida(a => (a === f.id ? null : f.id))}>
                          {faturaExpandida === f.id ? '−' : '+'}
                        </button>
                      </td>
                      <td><strong>{f.titular_nome || '—'}</strong><small className="table-secondary">SH #{f.software_house_id || '—'} · Fatura #{f.id}</small></td>
                      <td><Contato email={f.titular_email} whatsapp={f.titular_whatsapp} /></td>
                      <td>{formatarCompetencia(f.mes_referencia)}<small className="table-secondary">{f.tipo_cobranca}</small></td>
                      <td>{f.quantidade_aplicativos}</td>
                      <td><strong>{moedaBRL(f.valor)}</strong>{f.valor_pago != null && <small className="table-secondary">pago {moedaBRL(f.valor_pago)}</small>}</td>
                      <td>{f.vencimento_formatado}{Number(f.dias_atraso) > 0 && <span className="delay-badge mf-atraso">{f.dias_atraso} dia(s) de atraso</span>}</td>
                      <td>
                        <span className={`status-badge ${classeStatusFatura(f.status)}`}>{textoStatusFatura(f.status)}</span>
                        {f.data_pagamento_formatada && <small className="table-secondary">em {f.data_pagamento_formatada}</small>}
                        {erro && <small className="mf-erro">{erro}</small>}
                      </td>
                      <td>
                        <span className={`status-badge ${f.bloqueio_financeiro ? 'bloqueado' : 'ativo'}`}>{f.bloqueio_financeiro ? 'Bloqueado' : 'Liberado'}</span>
                      </td>
                      <td>
                        <div className="mf-acoes">
                          <AcoesFatura fatura={f} api={api} />
                          {f.software_house_id && <BotaoBloqueio id={f.software_house_id} nome={f.titular_nome} bloqueado={!!f.bloqueio_financeiro} />}
                        </div>
                      </td>
                    </tr>
                    {faturaExpandida === f.id && (
                      <tr className="fatura-detalhe-row">
                        <td colSpan="10">
                          <div className="mf-detalhe">
                            <dl>
                              <div><dt>Fatura</dt><dd>#{f.id}</dd></div>
                              <div><dt>ID no Asaas</dt><dd>{f.asaas_payment_id || '—'}</dd></div>
                              <div><dt>Gerada em</dt><dd>{f.data_geracao_formatada || '—'}</dd></div>
                              <div><dt>Última atualização</dt><dd>{f.atualizado_em || '—'}</dd></div>
                              <div><dt>Pagamento</dt><dd>{f.data_pagamento_formatada ? `${f.data_pagamento_formatada} · ${moedaBRL(f.valor_pago)}` : '—'}</dd></div>
                              <div><dt>Situação do titular</dt><dd>{f.titular_status || '—'}{f.bloqueio_financeiro ? ' · bloqueio financeiro' : ''}</dd></div>
                              {f.titular_cnpj && <div><dt>CNPJ</dt><dd>{f.titular_cnpj}</dd></div>}
                              {f.motivo_cancelamento && <div className="mf-wide"><dt>Cancelamento</dt><dd>{f.motivo_cancelamento}</dd></div>}
                              {erro && <div className="mf-wide"><dt>Erro na geração</dt><dd className="mf-erro">{erro}</dd></div>}
                              <div className="mf-wide"><dt>Descrição</dt><dd>{f.descricao}</dd></div>
                            </dl>
                            <div className="mf-detalhe-acoes">
                              {f.invoice_url && <a className="btn-secondary" href={f.invoice_url} target="_blank" rel="noopener noreferrer">Abrir fatura no Asaas</a>}
                              {(f.invoice_url || f.bank_slip_url) && <button type="button" className="btn-secondary" onClick={() => copiarLink(f)}>Copiar link para enviar</button>}
                              {podeCancelar && <button type="button" className="btn-danger-solid" disabled={ocupado} onClick={() => cancelarFatura(f)}>Cancelar fatura</button>}
                            </div>
                            <FaturaDetalhes fatura={f} api={api} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPaginas > 1 && (
          <div className="dashboard-pagination">
            <span>{faturasFiltradas.length} registro(s) · {POR_PAGINA} por página</span>
            <div>
              <button type="button" disabled={paginaAtual <= 1} onClick={() => setPagina(paginaAtual - 1)}>Anterior</button>
              <strong>Página {paginaAtual} de {totalPaginas}</strong>
              <button type="button" disabled={paginaAtual >= totalPaginas} onClick={() => setPagina(paginaAtual + 1)}>Próxima</button>
            </div>
          </div>
        )}
      </>}
    </section>
  );
}

const ROTULO_SITUACAO = {
  'a-gerar': 'A gerar',
  'sem-uso': 'Sem uso no mês',
  faturada: 'Faturada',
  erro: 'Erro na geração',
  cancelada: 'Cancelada'
};

function SituacaoFechamento({ linha }) {
  const { situacao, fatura } = linha;
  if (situacao === 'faturada' || situacao === 'cancelada') {
    return <>
      <span className={`status-badge ${classeStatusFatura(fatura.status)}`}>{textoStatusFatura(fatura.status)}</span>
      <small className="table-secondary">Fatura #{fatura.id}{Number(fatura.dias_atraso) > 0 ? ` · ${fatura.dias_atraso} dia(s) de atraso` : ''}</small>
    </>;
  }
  if (situacao === 'erro') {
    return <>
      <span className="status-badge bloqueado">Erro na geração</span>
      <small className="mf-erro">{erroDaFatura(fatura) || 'Falha ao gerar'}</small>
    </>;
  }
  if (situacao === 'a-gerar') return <span className="status-badge lancado">A gerar</span>;
  return <span className="mf-muted">Sem uso no mês</span>;
}

function AppsApurados({ aplicativos, competencia, valorUnitario }) {
  if (!aplicativos?.length) {
    return <p className="fatura-detalhe-msg">Nenhum aplicativo de produção com uso em {formatarCompetencia(competencia)}.</p>;
  }
  return (
    <section className="fatura-detalhe">
      <header><div><h4>Aplicativos apurados em {formatarCompetencia(competencia)}</h4><p>Ainda sem fatura gerada. Valor por app: {moedaBRL(valorUnitario)}.</p></div></header>
      <div className="fatura-apps" role="table" aria-label="Aplicativos apurados">
        <div className="fatura-apps-head" role="row">
          <span role="columnheader">Aplicativo</span>
          <span role="columnheader">Cliente</span>
          <span role="columnheader">Último uso</span>
          <span role="columnheader">Valor</span>
        </div>
        {aplicativos.map(app => (
          <div className="fatura-apps-row" role="row" key={app.id}>
            <span role="cell"><strong>{app.nome}</strong><small>#{app.id}</small></span>
            <span role="cell" data-label="Cliente">{app.cliente || '—'}</span>
            <span role="cell" data-label="Último uso">{dataHora(app.ultima_utilizacao)}</span>
            <span role="cell" data-label="Valor"><strong>{moedaBRL(valorUnitario)}</strong></span>
          </div>
        ))}
      </div>
    </section>
  );
}
